import { ProductName } from '@pypestream/api-services';
import {
  Divider,
  NavControls,
  NavExtra,
  NavLink,
  type NavLinkProps,
  NavLinkWC,
  NavLinks,
  TextSubtitle,
  UniversalNav,
  UniversalUserDropdown,
  UniversalUserDropdownTrigger,
  Avatar,
  Tooltip,
  Popover,
  TextOverline,
  Stack,
  TextBody,
  BlockList,
  Spacer,
  IconButton,
  Button,
} from '@pypestream/design-system';
import {
  type TransKeyProp,
  TranslationComponent,
  type TranslationKeys,
} from '@pypestream/translations';
import React, { type FC, useCallback, useMemo } from 'react';
import {
  type NavLinkProps as NavLinkPropsReactRouter,
  useHref,
  useLinkClickHandler,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { observer } from 'mobx-react-lite';

import { PublishButton, useDeleteSolutionModal, usePublishModal } from '..';
import {
  ProStudioPages,
  useCurrentRoute,
  useLoadingState,
  useAnalytics,
  useProStudioRoutes,
  useSolutionPresence,
} from '../../hooks';
import {
  sendUserEvent,
  useProStudioCtxSelector,
} from '../../xstate/app.xstate';
import { useSolutionTypePopover } from '../popovers';
import { ValidationPopover } from './validation-popover';
import { tableStore, ValidationMode } from '../../stores/table-store';
import { SolutionsDropdown } from './solutions-dropdown';

import './primary-nav.scss';
import { SolutionUserPresenceItem } from '../../xstate/prostudio.xstate.types';

type NavItem = {
  path: string;
  label: string;
  hidden: boolean;
  disabled?: boolean;
  i18nKey?: string;
};

export const NavLinkWrapper = React.forwardRef<
  NavLinkProps,
  TransKeyProp &
    NavLinkPropsReactRouter & {
      onClick?: React.MouseEventHandler<NavLinkWC>;
      children?: React.ReactNode;
      isActive?: boolean;
      disabled?: boolean;
      external?: boolean;
      label?: string;
    }
>(
  (
    {
      label,
      onClick,
      replace = false,
      state,
      target,
      to,
      children,
      i18nKey,
      isActive = false,
      disabled = false,
      external = false,
    },
    ref
  ) => {
    const href = useHref(to);
    const handleClick = useLinkClickHandler<NavLinkWC>(to, {
      replace,
      state,
      target,
    });

    return (
      <NavLink
        test-id={`nav-link-${label?.toLowerCase()}`}
        active={isActive}
        href={external ? String(to) : href}
        onClick={(event: React.MouseEvent<NavLinkWC, MouseEvent>) => {
          if (!external) {
            onClick?.(event);
            if (!event.defaultPrevented && !disabled) {
              handleClick(event);
            }
          }
        }}
        ref={ref}
        target={target}
        disabled={disabled}
      >
        {i18nKey ? (
          <TranslationComponent i18nKey={i18nKey as TranslationKeys}>
            {children}
          </TranslationComponent>
        ) : (
          children
        )}
      </NavLink>
    );
  }
);

export const PrimaryNav: FC = observer(() => {
  const navigate = useNavigate();
  const analytics = useAnalytics();
  const { pathname } = useLocation();
  const {
    draftVersionId,
    selectedBot,
    selectedProject,
    userInfo,
    // @todo: remove this once we have a proper way to get the account id on pro studio
    projectAccountId,
    showAgentCompilerErrors,
    solutionUserPresences,
    projectUsers,
  } = useProStudioCtxSelector((ctx) => ({
    selectedBot: ctx.selectedBot,
    draftVersionId: ctx.draftVersion,
    selectedProject: ctx.project?.id,
    userInfo: ctx.userInfo,
    projectAccountId: ctx.project?.accountId,
    showAgentCompilerErrors:
      ctx.featureFlags?.['agent-compiler-errors'] ?? false,
    solutionUserPresences: ctx.solutionUserPresences,
    projectUsers: ctx.projectUsers,
  }));

  const botType = selectedBot?.botType;
  const isLoading = useLoadingState();
  const isProcessing = isLoading || tableStore.isValidating;
  const routes = useProStudioRoutes();
  const { publishModal, openModal: openPublishModal } = usePublishModal({
    draftVersionId: draftVersionId ?? undefined,
  });
  const { deleteSolutionModal, openModal: openDeleteSolutionModal } =
    useDeleteSolutionModal();
  const { solutionTypePopover, solutionTypePopoverTriggerButton } =
    useSolutionTypePopover({
      disabled: isProcessing,
      onClose: openDeleteSolutionModal,
    });

  const isHomePage = useCurrentRoute().label === routes.home.label;
  const isConfigurationsPage =
    useCurrentRoute().label === ProStudioPages.CONFIGURATIONS;

  const isFirstLevelPage = isHomePage || isConfigurationsPage;
  const solutionId = selectedBot?.uniqueId;

  useSolutionPresence(isFirstLevelPage, solutionId, pathname);

  const navItems: NavItem[] = [
    // {
    //  ...routes.configurations,
    //  hidden: !selectedProject,
    //  label: 'Project Configurations',
    // },
    {
      ...routes.versions,
      hidden: isFirstLevelPage,
      i18nKey: 'pro-studio/navigation:nav_links.versions',
    },
    {
      ...routes.channels,
      hidden:
        isFirstLevelPage || !selectedBot || !botType || botType === 'survey',
      i18nKey: 'pro-studio/navigation:nav_links.channels',
    },
    {
      ...routes.files,
      hidden: isFirstLevelPage,
      i18nKey: 'pro-studio/navigation:nav_links.files',
    },
  ].filter((item) => item.hidden === false);

  const solutionItems: NavItem[] = [
    {
      ...routes.workflows,
      hidden: isFirstLevelPage,
      // disabled: !draftVersionId,
      i18nKey: 'pro-studio/navigation:nav_links.workflows',
    },
    {
      ...routes.actions,
      hidden: isFirstLevelPage,
      disabled: !draftVersionId,
      i18nKey: 'pro-studio/navigation:nav_links.actions',
    },
  ].filter((item) => item.hidden === false);

  const viewingListForCurrentPage = useMemo(() => {
    const list = solutionUserPresences ?? [];
    const isCurrentUser = (p: { user?: { id?: string } }) => {
      if (String(p.user?.id) === String(userInfo?.id)) return true;
    };
    return list.filter((p) => p.isActive && !isCurrentUser(p));
  }, [solutionUserPresences, userInfo?.id]);

  const handleValidate = useCallback(() => {
    tableStore.validateAndSync(ValidationMode.Manual);
  }, []);

  const getDisplayUserForPresence = (presence: SolutionUserPresenceItem) => {
    if (presence.user) return presence.user;
    const found = projectUsers?.find((pu) =>
      pu?.id ? String(pu.id) === String(presence.userId) : false
    );
    return found
      ? {
          id: found.id,
          firstName: found.firstName,
          lastName: found.lastName,
          picture: found.picture ?? undefined,
        }
      : null;
  };

  if (!userInfo?.defaultOrgId) {
    return null;
  }

  const isAutomaticValidation =
    tableStore.validationMode === ValidationMode.Auto;
  const isPublishButtonDisabled = isProcessing || !tableStore.canPublish;
  const publishButtonTooltipContent =
    isPublishButtonDisabled && isAutomaticValidation
      ? 'Fix any errors to enable publishing.'
      : isPublishButtonDisabled && !isAutomaticValidation
        ? 'Click Validate to check your changes, then fix any errors to enable publishing.'
        : undefined;

  const isValidateButtonDisabled =
    isProcessing || !tableStore.canValidateInManualMode;
  const validateButtonTooltipContent = isValidateButtonDisabled
    ? 'There are no changes to validate.'
    : 'Check your solution for errors.';

  return (
    <>
      <UniversalNav
        app={ProductName.ProStudio}
        slot="nav"
        orgId={projectAccountId}
        projectId={selectedProject}
        onManagerToolClick={(path) => {
          if (path.startsWith('http')) {
            window.location.href = path;
          } else {
            navigate(path);
          }
        }}
        onSelectionChange={(orgId) => {
          if (orgId) {
            // if (selectedOrgId && !selectedProject) {
            //   navigate(pathname.replace(selectedOrgId, orgId));
            // } else if (selectedProject) {
            //   navigate(`/organization/${orgId}/projects`);
            // } else {
            //   navigate(`/organization/${orgId}`);
            // }
          }
        }}
      >
        <SolutionsDropdown />
        <NavLinks slot="navigation">
          {navItems.map(({ path: to, label, i18nKey }) => (
            <NavLinkWrapper
              label={label}
              key={label}
              to={to}
              isActive={pathname === to}
              i18nKey={i18nKey as TranslationKeys}
            >
              {label}
            </NavLinkWrapper>
          ))}
        </NavLinks>

        {!isFirstLevelPage && viewingListForCurrentPage.length > 0 && (
          <NavExtra slot="extra">
            <div className="c-primary-nav__viewing-list">
              {(() => {
                const onCurrentRoute = viewingListForCurrentPage;
                const displayCount = 3;
                const visible = onCurrentRoute.slice(0, displayCount);
                const extraCount = onCurrentRoute.length - displayCount;

                return (
                  <>
                    {visible.map((presence) => {
                      const displayUser = getDisplayUserForPresence(presence);
                      const name = [
                        displayUser?.firstName,
                        displayUser?.lastName,
                      ]
                        .filter(Boolean)
                        .join(' ');
                      const displayName =
                        name || (displayUser ? 'Viewing' : 'Someone else');
                      return (
                        <Tooltip key={presence.id} content={displayName}>
                          <Avatar
                            className="c-primary-nav__viewing-list-item"
                            label={displayName}
                            src={displayUser?.picture ?? undefined}
                          />
                        </Tooltip>
                      );
                    })}
                    <IconButton
                      className="c-primary-nav__viewing-list-item"
                      id="viewers"
                      rounded
                    >
                      <Avatar>{extraCount > 0 ? `+${extraCount}` : '⋯'}</Avatar>
                    </IconButton>
                  </>
                );
              })()}
            </div>
            <Popover width="auto" trigger="viewers">
              <TextOverline variant="tertiary">
                Viewing this solution
              </TextOverline>
              <Spacer></Spacer>
              <BlockList gutter="large">
                {viewingListForCurrentPage.map((presence) => {
                  const displayUser = getDisplayUserForPresence(presence);
                  const name = [displayUser?.firstName, displayUser?.lastName]
                    .filter(Boolean)
                    .join(' ');
                  const displayName =
                    name || (displayUser ? 'Unknown' : 'Someone else');
                  return (
                    <Stack key={presence.id} alignItems="center" gutter="small">
                      <Avatar
                        label={displayName}
                        src={displayUser?.picture ?? undefined}
                      />
                      <TextBody>{displayName}</TextBody>
                    </Stack>
                  );
                })}
              </BlockList>
            </Popover>
          </NavExtra>
        )}
        {!isFirstLevelPage && (
          <>
            <NavControls slot="controls">
              <Stack nowrap alignItems="center" gutter="xsmall">
                {tableStore.validationMode === ValidationMode.Manual && (
                  <Tooltip content={validateButtonTooltipContent}>
                    <Button
                      variant="secondary"
                      size="small"
                      disabled={isValidateButtonDisabled}
                      onClick={handleValidate}
                    >
                      Validate
                    </Button>
                  </Tooltip>
                )}
                <ValidationPopover
                  isLoading={isProcessing}
                  errors={tableStore.errors}
                  showAgentCompilerErrors={showAgentCompilerErrors}
                />
                <TextSubtitle
                  test-id="ps-draft-label"
                  size="small"
                  variant="secondary"
                  truncate
                >
                  <TranslationComponent i18nKey="pro-studio/common:versions.draft">
                    Draft
                  </TranslationComponent>{' '}
                  {draftVersionId ? draftVersionId : 'v-'}
                </TextSubtitle>
              </Stack>
              <Divider vertical />
              {solutionItems.map(({ path: to, label, i18nKey, disabled }) => (
                <NavLinkWrapper
                  label={label}
                  key={label}
                  to={to}
                  isActive={pathname === to}
                  i18nKey={i18nKey as TranslationKeys}
                  disabled={disabled}
                >
                  {label}
                </NavLinkWrapper>
              ))}

              <PublishButton
                disabled={isPublishButtonDisabled}
                onClick={openPublishModal}
                tooltipContent={publishButtonTooltipContent}
              />
            </NavControls>

            <span slot="actions">{solutionTypePopoverTriggerButton}</span>
            {solutionTypePopover}
          </>
        )}

        <UniversalUserDropdownTrigger />

        <UniversalUserDropdown
          onPreferencesClick={() => {
            const defaultOrgPath = userInfo?.defaultOrgId
              ? `/organization/${userInfo?.defaultOrgId}`
              : '';

            const url = `${import.meta.env.FE_MANAGER_URL}${defaultOrgPath}/my-account`;

            window.location.href = url;
          }}
          onLogout={() => {
            if (analytics && typeof analytics.reset === 'function') {
              analytics.reset();
            }
            sendUserEvent({
              type: 'user.signOut',
            });
          }}
        />
      </UniversalNav>
      {deleteSolutionModal}
      {publishModal}
    </>
  );
});
